import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import FormLabel from '@mui/material/FormLabel'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import CloseFilledIcon from '@mui/icons-material/Close'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { GridColDef } from '@mui/x-data-grid/models/colDef'
import LegacyGoogleMapsAutocomplete from '@/javascript/presentation/components/inputs/legacy-google-maps-autocomplete'
import NumberField from '@/javascript/presentation/components/inputs/number-field'
import SelectionDataGrid from '@/javascript/presentation/components/data-grid/selection-data-grid'
import { openAccountMembersListPage } from '@/javascript/presentation/helpers/open-account-members-page'
import { useRoutingContext } from '../../hooks/use-routing-context'

const AccountMemberSelectionTable: React.FC = () => {
  const {
    selectedMembers, setSelectedMembers, memberConfigurations, setMemberConfigurations, selectionError
  } = useRoutingContext()
  const [options, setOptions] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<{ id: number; user: string } | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>, row: typeof selectedRow) => {
    setOptions(event.currentTarget)
    setSelectedRow(row)
  }

  const handleClose = () => {
    setOptions(null)
  }
  
  const handleRemoveMember = (memberId: number) => {
    setSelectedMembers(prev => prev.filter(member => member.id !== memberId))
    setMemberConfigurations(prev => {
      const updated = { ...prev }
      delete updated[memberId]
      return updated
    })
    handleClose()
  }

  const handleInitialAddressSelect = (memberId: number) => (place: google.maps.places.PlaceResult | null) => {
    setMemberConfigurations(prev => ({
      ...prev,
      [memberId]: {
        ...prev[memberId],
        initial: {
          latitude: place?.geometry?.location?.lat(), longitude: place?.geometry?.location?.lng(),
          address: place?.formatted_address
        }
      }
    }))
  }

  const handleFinalAddressSelect = (memberId: number) => (place: google.maps.places.PlaceResult | null) => {
    setMemberConfigurations(prev => ({
      ...prev,
      [memberId]: {
        ...prev[memberId],
        final: {
          latitude: place?.geometry?.location?.lat(), longitude: place?.geometry?.location?.lng(),
          address: place?.formatted_address
        }
      }
    }))
  }

  const handleWorkLimitChange = (memberId: number) => (value: number | null) => {
    setMemberConfigurations(prev => ({
      ...prev,
      [memberId]: { ...prev[memberId], workLimit: value }
    }))
  }

  const renderHeader = (params: any) => <FormLabel>{params.colDef.headerName}</FormLabel>

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'user',
      headerName: 'Usuário',
      flex: 1,
      renderHeader,
    },
    {
      field: 'workLimit',
      headerName: 'Máximo de atividades',
      flex: 1,
      renderHeader,
      renderCell: (params) => {
        return (
          <Box sx={{ width: '100%', py: 1 }}>
            <NumberField
              label=""
              min={1}
              max={100}
              size="small"
              value={memberConfigurations[params.row.id]?.workLimit || null}
              onValueChange={handleWorkLimitChange(params.row.id)}
              placeholder="Automático"
              integerOnly
            />
          </Box>
        )
      },
    },
    {
      field: 'initialAddress',
      headerName: 'Endereço inicial',
      flex: 1,
      renderHeader: (e) => <FormLabel required>{e.colDef.headerName}</FormLabel>,
      renderCell: (params) => {
        return (
          <Box sx={{ width: '100%', py: 1 }}>
            <LegacyGoogleMapsAutocomplete
              required
              initialAddress={memberConfigurations[params.row.id]?.initial?.address}
              onPlaceSelect={handleInitialAddressSelect(params.row.id)}
            />
          </Box>
        )
      },
    },
    {
      field: 'finalAddress',
      headerName: 'Endereço final',
      renderHeader: (e) => <FormLabel required>{e.colDef.headerName}</FormLabel>,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box sx={{ width: '100%', py: 1 }}>
            <LegacyGoogleMapsAutocomplete
              required
              initialAddress={memberConfigurations[params.row.id]?.final?.address}
              onPlaceSelect={handleFinalAddressSelect(params.row.id)}
            />
          </Box>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 80,
      sortable: false,
      filterable: false,
      renderHeader,
      renderCell: (params) => (
        <IconButton
          aria-label="more"
          id="long-button"
          aria-haspopup="true"
          onClick={(e) => handleClick(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [memberConfigurations])

  const rows = useMemo(() => selectedMembers.map((member) => ({
    id: member.id,
    user: member.user.completeData
  })), [selectedMembers])

  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 })

  return (
    <>
      {selectedMembers.length > 0 &&
        <>
          <SelectionDataGrid
            columns={columns}
            rows={rows}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowClassName={(params) => {
              const hasError = selectionError?.unassignedMemberIds?.includes(params.row.id)
              return hasError ? 'error-row' : ''
            }}
            sx={{
              '& .error-row': {
                color: 'error.main',
              },
            }}
          />
          <Menu
            anchorEl={options}
            open={Boolean(options)}
            onClose={handleClose}
            slotProps={{
              list: {
                'aria-labelledby': 'basic-button',
              },
            }}
          >
            <MenuItem
              onClick={() => selectedRow && handleRemoveMember(selectedRow.id)}
            >
              <ListItemIcon>
                <CloseFilledIcon fontSize="medium" />
              </ListItemIcon>
              <Typography variant="body1">Remover</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose()
                selectedRow && openAccountMembersListPage()
              }}
            >
              <ListItemIcon>
                <VisibilityRoundedIcon fontSize="medium" />
              </ListItemIcon>
              <Typography variant="body1">Ver detalhes</Typography>
            </MenuItem>
          </Menu>
        </>
        }
    </>
  )
}

export default AccountMemberSelectionTable
